import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Stack,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";

import type { Body_AccessToken as AccessToken } from "../types/Auth";

import useAuth, { isLoggedIn } from "../hooks/useAuth";

export const Route = createFileRoute("/login")({
  component: Login,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/projects",
      });
    }
  },
});

function Login() {
  const { loginMutation, error, resetError } = useAuth();
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  useEffect(() => {
    // Check if there's an auth error message from being logged out
    const storedAuthError = sessionStorage.getItem("authError");
    if (storedAuthError) {
      setAuthMessage(storedAuthError);
      sessionStorage.removeItem("authError");
    }
  }, []);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccessToken>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit: SubmitHandler<AccessToken> = async (data) => {
    if (isSubmitting) return;

    resetError();

    try {
      await loginMutation.mutateAsync(data);
    } catch {
      // error is handled by useAuth hook
    }
  };

  return (
    <>
      <Container
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "center",
          gap: 4,
          height: "100vh",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: "linear-gradient(145deg, #0f1420 0%, #141824 100%)",
            border: "1px solid rgba(59, 130, 246, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img
              src="/logo-full-dark.svg"
              alt="Glambot Studio"
              style={{ height: 60 }}
            />
          </Box>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 600,
              background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Login
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="textSecondary"
            sx={{ mb: 4 }}
          >
            Sign in to manage your projects
          </Typography>

          {authMessage && (
            <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setAuthMessage(null)}>
              {authMessage}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box>
            <Stack spacing={3}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                autoComplete="email"
                {...register("username", { required: true })}
                error={!!errors.username}
                helperText={errors.username?.message}
                required
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                autoComplete="current-password"
                {...register("password", { required: true })}
                error={!!errors.password}
                helperText={errors.password?.message}
                required
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  },
                }}
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
              
              <Box sx={{ textAlign: "center" }}>
                <MuiLink
                  component={Link}
                  to="/forgot-password"
                  sx={{ color: "#3b82f6", textDecoration: "none" }}
                >
                  Forgot Password?
                </MuiLink>
              </Box>

              <Divider sx={{ my: 2 }}>OR</Divider>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{" "}
                  <MuiLink
                    component={Link}
                    to="/register"
                    sx={{ color: "#3b82f6", textDecoration: "none" }}
                  >
                    Sign Up
                  </MuiLink>
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </>
  );
}
